import React, { Suspense, lazy, memo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './screens/Login';
import UpdatePassword from './screens/UpdatePassword';

// Lazy loading com preloading
const Home = lazy(() => import('./screens/Home'));
const ClientList = lazy(() => import('./screens/client/ClientList'));
const ClientForm = lazy(() => import('./screens/client/ClientForm'));
const ClientDetails = lazy(() => import('./screens/client/ClientDetails'));
const ClientContentControl = lazy(() => import('./screens/client/content/ClientContentControl'));
const SupplierList = lazy(() => import('./screens/supplier/SupplierList'));
const SupplierForm = lazy(() => import('./screens/supplier/SupplierForm'));
const SupplierDetails = lazy(() => import('./screens/supplier/SupplierDetails'));
const PaymentTemplateList = lazy(() => import('./screens/payment/PaymentTemplateList'));
const PaymentTemplateForm = lazy(() => import('./screens/payment/PaymentTemplateForm'));
const UserManagement = lazy(() => import('./screens/user/UserManagement'));
const UserTabs = lazy(() => import('./screens/user/UserTabs'));
const BookingList = lazy(() => import('./screens/booking/BookingList'));
const IndexStatus = lazy(() => import('./screens/IndexStatus'));
const InviteList = lazy(() => import('./screens/invite/InviteList'));
const InviteForm = lazy(() => import('./screens/invite/InviteForm'));
const ClientPaymentDemo = lazy(() => import('./screens/demo/ClientPaymentDemo'));
const CurrencyList = lazy(() => import('./screens/currency/CurrencyList'));
const LanguageList = lazy(() => import('./screens/language/LanguageList'));
const FooterI18nManager = lazy(() => import('./screens/client/FooterI18nManager'));
const UserPoolList = lazy(() => import('./screens/cognito/UserPoolList'));
const UserPoolForm = lazy(() => import('./screens/cognito/UserPoolForm'));
const UserPoolOriginList = lazy(() => import('./screens/cognito/UserPoolOriginList'));
const UserPoolOriginForm = lazy(() => import('./screens/cognito/UserPoolOriginForm'));

// Preload componentes mais usados
if (typeof window !== 'undefined') {
  const preloadComponents = () => {
    import('./screens/client/ClientList');
    import('./screens/supplier/SupplierList');
  };
  setTimeout(preloadComponents, 2000);
}

const LoadingSpinner = memo(() => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
));

const ProtectedLayout = memo<{ children: React.ReactNode }>(({ children }) => (
  <ProtectedRoute>
    <Layout>
      <Suspense fallback={<LoadingSpinner />}>
        {children}
      </Suspense>
    </Layout>
  </ProtectedRoute>
));

const App = memo(() => {
  console.log('🏠 App: Componente renderizado em:', new Date().toISOString());
  
  return (
    <Router>
      <AuthProvider>
        <div className="App min-vh-100">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route path="/" element={<ProtectedLayout><Home /></ProtectedLayout>} />
            <Route path="/clients" element={<ProtectedLayout><ClientList /></ProtectedLayout>} />
            <Route path="/clients/new" element={<ProtectedLayout><ClientForm /></ProtectedLayout>} />
            <Route path="/clients/edit/:id" element={<ProtectedLayout><ClientForm /></ProtectedLayout>} />
            <Route path="/clients/:id/details" element={<ProtectedLayout><ClientDetails /></ProtectedLayout>} />
            <Route path="/clients/:id/content-control" element={<ProtectedLayout><ClientContentControl /></ProtectedLayout>} />
            <Route path="/suppliers" element={<ProtectedLayout><SupplierList /></ProtectedLayout>} />
            <Route path="/suppliers/new" element={<ProtectedLayout><SupplierForm /></ProtectedLayout>} />
            <Route path="/suppliers/edit/:id" element={<ProtectedLayout><SupplierForm /></ProtectedLayout>} />
            <Route path="/suppliers/:id/details" element={<ProtectedLayout><SupplierDetails /></ProtectedLayout>} />
            <Route path="/payment-templates" element={<ProtectedLayout><PaymentTemplateList /></ProtectedLayout>} />
            <Route path="/payment-templates/new" element={<ProtectedLayout><PaymentTemplateForm /></ProtectedLayout>} />
            <Route path="/payment-templates/edit/:id" element={<ProtectedLayout><PaymentTemplateForm /></ProtectedLayout>} />
            <Route path="/users" element={<ProtectedLayout><UserTabs /></ProtectedLayout>} />
            <Route path="/bookings" element={<ProtectedLayout><BookingList /></ProtectedLayout>} />
            <Route path="/invites" element={<ProtectedLayout><InviteList /></ProtectedLayout>} />
            <Route path="/invites/new" element={<ProtectedLayout><InviteForm /></ProtectedLayout>} />
            <Route path="/payment-demo" element={<ProtectedLayout><ClientPaymentDemo /></ProtectedLayout>} />
            <Route path="/index-status" element={<ProtectedLayout><IndexStatus /></ProtectedLayout>} />
            <Route path="/currencies" element={<ProtectedLayout><CurrencyList /></ProtectedLayout>} />
            <Route path="/languages" element={<ProtectedLayout><LanguageList /></ProtectedLayout>} />
            <Route path="/sub-clients/:subClientId/:subClientUrl/footer-i18n" element={<ProtectedLayout><FooterI18nManager /></ProtectedLayout>} />
            <Route path="/cognito/user-pools" element={<ProtectedLayout><UserPoolList /></ProtectedLayout>} />
            <Route path="/cognito/user-pools/new" element={<ProtectedLayout><UserPoolForm /></ProtectedLayout>} />
            <Route path="/cognito/user-pools/:id/edit" element={<ProtectedLayout><UserPoolForm /></ProtectedLayout>} />
            <Route path="/cognito/user-pool-origins" element={<ProtectedLayout><UserPoolOriginList /></ProtectedLayout>} />
            <Route path="/cognito/user-pool-origins/new" element={<ProtectedLayout><UserPoolOriginForm /></ProtectedLayout>} />
            <Route path="/cognito/user-pool-origins/:id/edit" element={<ProtectedLayout><UserPoolOriginForm /></ProtectedLayout>} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
});

export default App;
